import { useState, useEffect, useCallback } from 'react';
import { DollarSign, CheckCircle, XCircle, Clock, Users, CreditCard, ExternalLink, Send } from 'lucide-react';
import api from '../utils/api';

export default function PayoutsTab({ headers }) {
  const [payouts, setPayouts] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, c] = await Promise.all([
        api.get('/affiliate/admin/payout-requests', { headers }),
        api.get('/affiliate/leaderboard', { headers }),
        api.get('/connect/accounts', { headers }).catch(() => ({ data: [] })),
      ]);
      setPayouts(p.data);
      setAffiliates(a.data);
      setConnectedAccounts(c.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const approvePayout = async (id) => {
    try {
      await api.post(`/affiliate/admin/approve-payout/${id}`, {}, { headers });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const rejectPayout = async (id) => {
    try {
      await api.post(`/affiliate/admin/reject-payout/${id}`, {}, { headers });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const transferPayout = async (id) => {
    try {
      const res = await api.post(`/connect/transfer/${id}`, {}, { headers });
      if (res.data.status === 'transferred') {
        fetchData();
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Transfer failed');
    }
  };

  const openDashboard = async (code) => {
    try {
      const res = await api.get(`/connect/dashboard-link/${code}`, { headers });
      window.open(res.data.url, '_blank');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to get dashboard link');
    }
  };

  if (loading) return <p className="text-muted-foreground text-sm">Loading payouts...</p>;

  const totalPending = payouts.reduce((s, p) => s + (p.amount || 0), 0);
  const connectedCount = connectedAccounts.filter(a => a.stripe_onboarding_complete).length;

  return (
    <div className="space-y-8" data-testid="payouts-tab">
      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-sm p-4">
          <DollarSign className="w-4 h-4 text-primary mb-1" />
          <p className="font-serif text-2xl text-primary">${totalPending.toFixed(2)}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Pending Payouts</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-sm p-4">
          <Clock className="w-4 h-4 text-amber-400 mb-1" />
          <p className="font-serif text-2xl text-amber-400">{payouts.length}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Requests</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-sm p-4">
          <Users className="w-4 h-4 text-primary mb-1" />
          <p className="font-serif text-2xl text-primary">{affiliates.length}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Affiliates</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-sm p-4">
          <CreditCard className="w-4 h-4 text-green-400 mb-1" />
          <p className="font-serif text-2xl text-green-400">{connectedCount}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Stripe Connected</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-sm p-4">
          <DollarSign className="w-4 h-4 text-green-400 mb-1" />
          <p className="font-serif text-2xl text-green-400">${affiliates.reduce((s, a) => s + (a.total_earnings || 0), 0).toFixed(2)}</p>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Total Commissions</p>
        </div>
      </div>

      {/* Pending Payouts */}
      <div>
        <h3 className="font-serif text-lg mb-4">Pending Payout Requests</h3>
        {payouts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending payout requests.</p>
        ) : (
          <div className="space-y-2">
            {payouts.map(p => (
              <div key={p.id} className="bg-white/5 border border-white/10 rounded-sm p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`payout-${p.id}`}>
                <div>
                  <p className="font-serif text-sm">${p.amount?.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">Code: {p.affiliate_code} &middot; {new Date(p.requested_at).toLocaleDateString()}
                    {p.transferred && <span className="text-green-400 ml-1">Transferred</span>}
                  </p>
                </div>
                <div className="flex gap-2">
                  {p.status === 'pending' && (
                    <>
                      <button onClick={() => approvePayout(p.id)} className="flex items-center gap-1 text-xs text-green-400 hover:underline" data-testid={`approve-payout-${p.id}`}>
                        <CheckCircle className="w-3 h-3" /> Approve
                      </button>
                      <button onClick={() => rejectPayout(p.id)} className="flex items-center gap-1 text-xs text-red-400 hover:underline" data-testid={`reject-payout-${p.id}`}>
                        <XCircle className="w-3 h-3" /> Reject
                      </button>
                    </>
                  )}
                  {p.status === 'approved' && !p.transferred && (
                    <button onClick={() => transferPayout(p.id)} className="flex items-center gap-1 text-xs text-primary hover:underline font-semibold" data-testid={`transfer-payout-${p.id}`}>
                      <Send className="w-3 h-3" /> Transfer via Stripe
                    </button>
                  )}
                  {p.transferred && (
                    <span className="text-[10px] text-green-400 font-bold uppercase tracking-wider">Paid</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connected Accounts */}
      {connectedAccounts.length > 0 && (
        <div>
          <h3 className="font-serif text-lg mb-4">Stripe Connected Accounts</h3>
          <div className="space-y-2">
            {connectedAccounts.map((a, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-4 flex items-center justify-between" data-testid={`connected-${a.code}`}>
                <div>
                  <p className="font-serif text-sm">{a.name} <span className="text-xs text-muted-foreground">({a.code})</span></p>
                  <p className="text-xs text-muted-foreground">
                    {a.stripe_onboarding_complete ? (
                      <span className="text-green-400">Onboarding complete</span>
                    ) : (
                      <span className="text-amber-400">Onboarding pending</span>
                    )}
                    {a.stripe_account_id && <span className="ml-1">· {a.stripe_account_id}</span>}
                  </p>
                </div>
                {a.stripe_onboarding_complete && (
                  <button onClick={() => openDashboard(a.code)} className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`dashboard-link-${a.code}`}>
                    <ExternalLink className="w-3 h-3" /> Stripe Dashboard
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Affiliate Leaderboard */}
      <div>
        <h3 className="font-serif text-lg mb-4">Top Affiliates</h3>
        <div className="space-y-2">
          {affiliates.map((a, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-4 flex items-center justify-between" data-testid={`affiliate-${i}`}>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground font-mono w-6">#{i + 1}</span>
                <div>
                  <p className="font-serif text-sm">{a.name} <span className={`text-[10px] font-bold uppercase tracking-wider ml-1 ${a.tier === 'Platinum' ? 'text-cyan-300' : a.tier === 'Gold' ? 'text-yellow-400' : a.tier === 'Silver' ? 'text-gray-400' : 'text-amber-600'}`}>{a.tier}</span></p>
                  <p className="text-xs text-muted-foreground">{a.code} &middot; {a.total_clicks || 0} clicks &middot; {a.total_conversions || 0} conversions</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-primary font-semibold">${(a.total_earnings || 0).toFixed(2)}</p>
                <p className="text-[10px] text-muted-foreground">${(a.pending_earnings || 0).toFixed(2)} pending</p>
              </div>
            </div>
          ))}
          {affiliates.length === 0 && <p className="text-sm text-muted-foreground">No affiliates yet.</p>}
        </div>
      </div>
    </div>
  );
}
